import { useCallback, useEffect, useMemo } from 'react';
import uniq from 'lodash/uniq';
import { fireIssueCreateErrorAnalytics as fireErrorAnalytics } from '@atlassian/jira-issue-create-analytics-common/src/utils/fire-error-analytics/index.tsx';
import { GIC_LIFECYCLE_EVENTS } from '@atlassian/jira-issue-create-extensibility/src/common/utils/lifecycle-events/constants.tsx';
import { useGICLifeCycleEvents } from '@atlassian/jira-issue-create-extensibility/src/common/utils/lifecycle-events/main.tsx';
import {
	fireOperationalAnalytics,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import type { BrowserStorageCleanup } from '../../common/types.tsx';
import { useBrowserStorageCleanupService } from '../../services/browser-storage-cleanup/index.tsx';
import { useInProgressIssueService } from '../../services/in-progress-issue/index.tsx';
import {
	useInProgressIssue,
	useInProgressIssueActions,
	useIsModalOpen,
} from '../in-progress-issue/index.tsx';
import { useIssueRestoreMeta } from '../issue-restore-meta/index.tsx';
import { useOnWindowFocus } from './utils.tsx';

export function useBrowserStorageCleanupController() {
	const browserStorageCleanupService = useBrowserStorageCleanupService();
	const [{ inProgressIssueMetas }] = useIssueRestoreMeta();
	const { setRestoreError } = useInProgressIssueActions();

	const sessionInProgressIssueIDs = useMemo(
		() =>
			inProgressIssueMetas
				?.filter((item) => item.shouldRestore)
				.map((item) => item.inProgressIssueID) || [],
		[inProgressIssueMetas],
	);
	const [, { onReinsertIntoBrowserStorage }] = useInProgressIssue();
	const inProgressIssueService = useInProgressIssueService();

	const [isOpen] = useIsModalOpen();

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const onModalOpened = useCallback(
		async ({ inProgressIssueID }: { inProgressIssueID: string }) => {
			const browserStorageCleanup = await browserStorageCleanupService
				.getBrowserStorageCleanup() // eslint-disable-next-line @typescript-eslint/no-explicit-any
				.catch((error: any) => {
					fireErrorAnalytics({
						meta: {
							id: 'useBrowserStorageCleanupController',
							packageName: 'jiraIssueCreateRestore',
						},
						error,
						attributes: {
							eventName: 'onModalOpened',
							stage: 'getBrowserStorageCleanup',
						},
						sendToPrivacyUnsafeSplunk: true,
					});
					setRestoreError && setRestoreError(error);
				});

			const newBrowserStorageCleanup: BrowserStorageCleanup = {
				...(browserStorageCleanup || {}),
				inProgressIssueIDs: uniq([
					...(browserStorageCleanup?.inProgressIssueIDs || []),
					inProgressIssueID,
				]),
			};
			if (browserStorageCleanup) {
				await browserStorageCleanupService
					.updateBrowserStorageCleanup(newBrowserStorageCleanup) // eslint-disable-next-line @typescript-eslint/no-explicit-any
					.catch((error: any) => {
						fireErrorAnalytics({
							meta: {
								id: 'useBrowserStorageCleanupController',
								packageName: 'jiraIssueCreateRestore',
							},
							error,
							attributes: {
								eventName: 'onModalOpened',
								stage: 'updateBrowserStorageCleanup',
							},
							sendToPrivacyUnsafeSplunk: true,
						});
						setRestoreError && setRestoreError(error);
					});
			} else {
				await browserStorageCleanupService
					.createBrowserStorageCleanup(newBrowserStorageCleanup) // eslint-disable-next-line @typescript-eslint/no-explicit-any
					.catch((error: any) => {
						fireErrorAnalytics({
							meta: {
								id: 'useBrowserStorageCleanupController',
								packageName: 'jiraIssueCreateRestore',
							},
							error,
							attributes: {
								eventName: 'onModalOpened',
								stage: 'createBrowserStorageCleanup',
							},
							sendToPrivacyUnsafeSplunk: true,
						});
						setRestoreError && setRestoreError(error);
					});
			}
		},
		[browserStorageCleanupService, setRestoreError],
	);

	const onModalClosed = useCallback(
		async ({ inProgressIssueID }: { inProgressIssueID: string }) => {
			const browserStorageCleanup = await browserStorageCleanupService
				.getBrowserStorageCleanup() // eslint-disable-next-line @typescript-eslint/no-explicit-any
				.catch((error: any) => {
					fireErrorAnalytics({
						meta: {
							id: 'useBrowserStorageCleanupController',
							packageName: 'jiraIssueCreateRestore',
						},
						error,
						attributes: {
							eventName: 'onModalClosed',
							stage: 'getBrowserStorageCleanup',
						},
						sendToPrivacyUnsafeSplunk: true,
					});
					setRestoreError && setRestoreError(error);
				});
			if (!browserStorageCleanup) {
				return;
			}
			const newBrowserStorageCleanup = {
				...browserStorageCleanup,
				inProgressIssueIDs: sessionInProgressIssueIDs.filter((id) => id !== inProgressIssueID),
			};
			// The inProgressIssueID from the onModalClosed event will be cleaned up by the standard in-progress-issue onModalClosed
			const inProgressIssueIDsToRemove = browserStorageCleanup.inProgressIssueIDs.filter(
				(id) => !sessionInProgressIssueIDs.includes(id),
			);

			await Promise.all([
				browserStorageCleanupService.updateBrowserStorageCleanup(newBrowserStorageCleanup),
				...inProgressIssueIDsToRemove.map((id) => inProgressIssueService.deleteInProgressIssue(id)),
			]) // eslint-disable-next-line @typescript-eslint/no-explicit-any
				.catch((error: any) => {
					fireErrorAnalytics({
						meta: {
							id: 'useBrowserStorageCleanupController',
							packageName: 'jiraIssueCreateRestore',
						},
						error,
						attributes: {
							eventName: 'onModalClosed',
							stage: 'promiseAll',
						},
						sendToPrivacyUnsafeSplunk: true,
					});
					setRestoreError && setRestoreError(error);
				});
			fireOperationalAnalytics(createAnalyticsEvent({}), 'browserStorageCleanup onModalClosed', {
				inProgressIssueIDsToRemoveLength: inProgressIssueIDsToRemove.length,
			});
		},
		[
			browserStorageCleanupService,
			createAnalyticsEvent,
			inProgressIssueService,
			sessionInProgressIssueIDs,
			setRestoreError,
		],
	);

	const onFocusWindow = useCallback(async () => {
		if (!isOpen) {
			return;
		}

		// Bulk Create will need to update this for multiple
		const browserStorageCleanup = await browserStorageCleanupService
			.getBrowserStorageCleanup()
			.catch((error) => {
				fireErrorAnalytics({
					meta: {
						id: 'useBrowserStorageCleanupController',
						packageName: 'jiraIssueCreateRestore',
					},
					error,
					attributes: {
						eventName: 'onFocusWindow',
						stage: 'getBrowserStorageCleanup',
					},
					sendToPrivacyUnsafeSplunk: true,
				});
				setRestoreError && setRestoreError(error);
			});

		const missingInProgressIssueIDs =
			browserStorageCleanup && sessionInProgressIssueIDs.length
				? sessionInProgressIssueIDs.filter(
						(id) => !browserStorageCleanup.inProgressIssueIDs.includes(id),
					)
				: [];
		if (missingInProgressIssueIDs.length) {
			await onReinsertIntoBrowserStorage();
			const newBrowserStorageCleanup = {
				...browserStorageCleanup,
				inProgressIssueIDs: uniq([
					...(browserStorageCleanup?.inProgressIssueIDs || []),
					...missingInProgressIssueIDs,
				]),
			};
			await browserStorageCleanupService
				.updateBrowserStorageCleanup(newBrowserStorageCleanup) // eslint-disable-next-line @typescript-eslint/no-explicit-any
				.catch((error: any) => {
					fireErrorAnalytics({
						meta: {
							id: 'useBrowserStorageCleanupController',
							packageName: 'jiraIssueCreateRestore',
						},
						error,
						attributes: {
							eventName: 'onFocusWindow',
							stage: 'updateBrowserStorageCleanup',
						},
						sendToPrivacyUnsafeSplunk: true,
					});
					setRestoreError && setRestoreError(error);
				});
		}
		if (missingInProgressIssueIDs.length) {
			fireOperationalAnalytics(createAnalyticsEvent({}), 'browserStorageCleanup onFocusWindow', {
				missingInProgressIssueIDsLength: missingInProgressIssueIDs.length,
			});
		}
	}, [
		browserStorageCleanupService,
		sessionInProgressIssueIDs,
		setRestoreError,
		onReinsertIntoBrowserStorage,
		createAnalyticsEvent,
		isOpen,
	]);

	return {
		onModalOpened,
		onModalClosed,
		onFocusWindow,
	};
}

export function useBrowserStorageCleanup() {
	const { subscribe } = useGICLifeCycleEvents();
	const { onModalOpened, onModalClosed, onFocusWindow } = useBrowserStorageCleanupController();

	useEffect(
		() => subscribe(GIC_LIFECYCLE_EVENTS.MODAL_OPENED, onModalOpened),
		[onModalOpened, subscribe],
	);

	useEffect(
		() => subscribe(GIC_LIFECYCLE_EVENTS.MODAL_CLOSED, onModalClosed),
		[onModalClosed, subscribe],
	);

	useOnWindowFocus(onFocusWindow);
}
